import { useRef, useState } from "react"
import { useLingui } from "@lingui/react/macro"
import { PortalPopover } from "@/components/v2/chrome/portal-popover"
import type {
  ActiveFacet,
  FacetGroup,
} from "@/lib/activity-facets"

// Event-facet row for the DEAL activity timeline (CON-146,
// Paper "CRM - Search"). Sits between the kind chips and the
// feed on the deal surfaces only (page + drawer). A leading
// funnel glyph + one dropdown pill per distinct facet field
// present among the loaded events. Selecting a value filters
// the feed CLIENT-SIDE to events whose field+label match
// (other kinds untouched — see `filterByFacet`).
//
// DATA-DRIVEN: the caller hands us the `groups` extracted from
// the loaded items (`extractFacetGroups`), so the pills mirror
// exactly the deal fields that actually changed — Stage /
// Value / Status / Name today, and Owner the moment owner-
// change tracking lands server-side. Renders nothing when no
// event carries a usable facet.

interface ActivityFacetRowProps {
  groups: FacetGroup[]
  active: ActiveFacet | null
  onChange: (active: ActiveFacet | null) => void
}

export function ActivityFacetRow({
  groups,
  active,
  onChange,
}: ActivityFacetRowProps) {
  const { t } = useLingui()
  if (groups.length === 0) return null

  return (
    <div
      data-testid="activity-facet-row"
      className="flex items-center gap-1.5 flex-wrap"
      role="group"
      aria-label={t`Activity facets`}
    >
      <FunnelIcon />
      {groups.map((group) => (
        <FacetPill
          key={group.field}
          group={group}
          activeValue={
            active && active.field === group.field ? active.toLabel : null
          }
          onSelect={(toLabel) => onChange({ field: group.field, toLabel })}
          onClear={() => onChange(null)}
        />
      ))}
    </div>
  )
}

function FacetPill({
  group,
  activeValue,
  onSelect,
  onClear,
}: {
  group: FacetGroup
  activeValue: string | null
  onSelect: (toLabel: string) => void
  onClear: () => void
}) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isActive = activeValue != null

  // Active pill shows the chosen value + a clear affordance; a
  // resting pill shows the field label + a chevron and opens
  // the value dropdown.
  const base =
    "inline-flex items-center rounded-full h-6 pl-2.5 gap-1 text-[12px] leading-4 cursor-pointer transition-colors"
  const tone = isActive
    ? "bg-[#ECE7DE] text-foreground font-medium pr-1"
    : "bg-[#F0EDE6] text-[#5C544A] hover:bg-[#E8E2D6] pr-2"

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`facet-pill-${group.field}`}
        className={`${base} ${tone}`}
      >
        <span>{isActive ? activeValue : group.label}</span>
        {isActive ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={t`Clear ${group.label} filter`}
            data-testid={`facet-clear-${group.field}`}
            onClick={(event) => {
              // Don't toggle the dropdown open — clearing is its
              // own action distinct from re-opening the list.
              event.stopPropagation()
              onClear()
            }}
            className="flex items-center justify-center rounded-full size-4 hover:bg-[#DCD5C7]"
          >
            <ClearIcon />
          </span>
        ) : (
          <ChevronDownIcon />
        )}
      </button>
      <PortalPopover
        open={open}
        onClose={() => setOpen(false)}
        triggerRef={triggerRef}
        placement="bottom-start"
        offset={6}
        // The deal drawer panel is z-50; the facet dropdown
        // opens from inside it, so it must sit above.
        zClassName="z-[60]"
        ariaLabel={t`Filter by ${group.label}`}
        className="min-w-[160px] max-w-[260px] rounded-xl bg-white border border-[#E5DFD3] py-1.5 shadow-[0_12px_32px_rgba(30,27,23,0.14)]"
      >
        <ul role="listbox" className="flex flex-col">
          {group.values.map((value) => {
            const selected = value.toLabel === activeValue
            return (
              <li key={value.toLabel} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => {
                    // Picking the active value again clears it;
                    // otherwise select it.
                    if (selected) {
                      onClear()
                    } else {
                      onSelect(value.toLabel)
                    }
                    setOpen(false)
                  }}
                  data-testid={`facet-option-${group.field}-${value.toLabel}`}
                  className={`flex items-center w-full text-left px-3 py-1.5 gap-2 text-[13px] leading-4 hover:bg-[#F7F5F0] ${
                    selected ? "font-medium text-foreground" : "text-[#5C544A]"
                  }`}
                >
                  <span className="grow min-w-0 truncate">
                    {value.toLabel}
                  </span>
                  {selected ? <CheckIcon /> : null}
                </button>
              </li>
            )
          })}
        </ul>
      </PortalPopover>
    </>
  )
}

function FunnelIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        d="M2.5 3.5H13.5L9.25 8.5V12.5L6.75 13.5V8.5L2.5 3.5Z"
        stroke="#9A938A"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ChevronDownIcon() {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        d="M4 6L8 10L12 6"
        stroke="#7A746B"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ClearIcon() {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 4L12 12M12 4L4 12"
        stroke="#5C544A"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-foreground"
    >
      <path
        d="M3.5 8.5L6.5 11.5L12.5 4.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
